import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Pause, RotateCcw, CheckCircle } from 'lucide-react';
import { TertiaryButton, PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import Container from '../components/brand/BrandLayout';
import { SessionCompletion } from '../components/SessionCompletion';

/**
 * Box Breathing - 4-4-4-4 Pattern
 * 
 * A calming breathing exercise with visual guidance.
 * Inhale 4s → Hold 4s → Exhale 4s → Hold 4s
 */

const PHASE_DURATION = 4000; // 4 seconds per phase

const phases = [
    { name: 'Inhale', instruction: 'Breathe in slowly', scale: 1.5 },
    { name: 'Hold', instruction: 'Hold gently', scale: 1.5 },
    { name: 'Exhale', instruction: 'Release slowly', scale: 1 },
    { name: 'Hold', instruction: 'Rest here', scale: 1 }
];

const BoxBreathing = () => {
    const navigate = useNavigate();
    const [isRunning, setIsRunning] = useState(false);
    const [currentPhase, setCurrentPhase] = useState(0);
    const [progress, setProgress] = useState(0);
    const [cycleCount, setCycleCount] = useState(0);
    const [showCompletion, setShowCompletion] = useState(false);

    const phase = phases[currentPhase];

    // Progress timer
    useEffect(() => {
        if (!isRunning) return;

        const startTime = Date.now();
        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const phaseProgress = Math.min(elapsed / PHASE_DURATION, 1);
            setProgress(phaseProgress);

            if (elapsed >= PHASE_DURATION) {
                // Move to next phase
                setCurrentPhase(prev => {
                    const next = (prev + 1) % 4;
                    if (next === 0) {
                        setCycleCount(c => c + 1);
                    }
                    return next;
                });
                setProgress(0);
            }
        }, 50);

        return () => clearInterval(interval);
    }, [isRunning, currentPhase]);

    const handleStart = () => {
        setIsRunning(true);
    };

    const handlePause = () => {
        setIsRunning(false);
    };

    const handleReset = () => {
        setIsRunning(false);
        setCurrentPhase(0);
        setProgress(0);
        setCycleCount(0);
    };

    // Calculate current scale based on phase and progress
    const getScale = () => {
        const fromScale = phases[(currentPhase + 3) % 4].scale;
        const toScale = phase.scale;
        
        // Only animate scale during inhale (0) and exhale (2)
        if (currentPhase === 0) {
            // Inhale: grow from 1 to 1.5
            return 1 + (0.5 * progress);
        } else if (currentPhase === 2) {
            // Exhale: shrink from 1.5 to 1
            return 1.5 - (0.5 * progress);
        }
        // Hold phases: stay at current scale
        return toScale;
    };

    const scale = getScale();
    const countdown = Math.ceil(PHASE_DURATION / 1000 - (progress * PHASE_DURATION / 1000));

    return (
        <div className="min-h-screen flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header">
                <Container size="lg" className="py-4">
                    <div className="flex items-center justify-between">
                        <button 
                            onClick={() => navigate('/home?tab=regulation')}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <span className="font-medium text-[#1D6B6B]">Box Breathing</span>
                        <SmallText className="text-[#7D8E95] min-w-[80px] text-right">
                            {cycleCount > 0 ? `${cycleCount} cycle${cycleCount > 1 ? 's' : ''}` : ''}
                        </SmallText>
                    </div>
                </Container>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Title */}
                <div className="text-center mb-12">
                    <Heading as="h1" size="text-2xl" className="text-[#1D6B6B] mb-2">
                        Box Breathing
                    </Heading>
                    <BodyText className="text-[#7D8E95]">
                        4 seconds each: inhale, hold, exhale, hold
                    </BodyText>
                </div>

                {/* Breathing Box */}
                <div className="relative w-64 h-64 flex items-center justify-center mb-12">
                    {/* Outer guide box */}
                    <div 
                        className="absolute inset-0 rounded-3xl border-2 border-dashed border-white/40 opacity-50"
                        style={{ transform: 'scale(1.5)' }}
                    />
                    
                    {/* Animated breathing box */}
                    <div
                        className="w-32 h-32 rounded-3xl transition-colors duration-500"
                        style={{
                            transform: `scale(${scale})`,
                            transition: isRunning ? 'transform 0.1s linear' : 'transform 0.5s ease-out',
                            background: currentPhase === 0 || currentPhase === 1
                                ? 'linear-gradient(135deg, #7D8E95 0%, #8FB89F 100%)'
                                : 'linear-gradient(135deg, #7D8E95 0%, #7D8E95 100%)',
                            boxShadow: `0 8px 32px ${currentPhase === 0 || currentPhase === 1 
                                ? 'rgba(52,70,72,0.3)' 
                                : 'rgba(125,142,149,0.3)'}`
                        }}
                    />

                    {/* Center content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10">
                        {isRunning ? (
                            <>
                                <span className="text-4xl font-light text-white mb-1">
                                    {countdown}
                                </span>
                                <span className="text-lg font-medium text-white/90">
                                    {phase.name}
                                </span>
                            </>
                        ) : (
                            <span className="text-lg font-medium text-white/80">
                                {cycleCount > 0 ? 'Paused' : 'Ready'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Instruction */}
                <div className="text-center mb-12 h-12">
                    {isRunning && (
                        <BodyText className="text-[#7D8E95] animate-fade-in">
                            {phase.instruction}
                        </BodyText>
                    )}
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4">
                    {!isRunning ? (
                        <PrimaryButton onClick={handleStart} size="lg">
                            <Play className="w-5 h-5 mr-2" />
                            {cycleCount > 0 ? 'Resume' : 'Start'}
                        </PrimaryButton>
                    ) : (
                        <SecondaryButton onClick={handlePause} size="lg">
                            <Pause className="w-5 h-5 mr-2" />
                            Pause
                        </SecondaryButton>
                    )}
                    
                    {cycleCount >= 3 && !isRunning && (
                        <PrimaryButton 
                            onClick={() => setShowCompletion(true)} 
                            size="lg"
                            className="bg-[#1D6B6B] hover:bg-[#1D6B6B]"
                        >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Finish
                        </PrimaryButton>
                    )}
                    
                    {(cycleCount > 0 || currentPhase > 0) && (
                        <TertiaryButton onClick={handleReset}>
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reset
                        </TertiaryButton>
                    )}
                </div>

                {/* Phase indicators */}
                <div className="flex items-center gap-2 mt-12">
                    {phases.map((p, index) => (
                        <div
                            key={index}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                index === currentPhase && isRunning
                                    ? 'bg-[#1D6B6B] scale-125'
                                    : index < currentPhase || (currentPhase === 0 && index === 3 && cycleCount > 0)
                                        ? 'bg-[#7D8E95]'
                                        : 'bg-[#FFBB98]'
                            }`}
                        />
                    ))}
                </div>

                {/* Gentle reminder */}
                <SmallText className="text-[#7D8E95] mt-8 text-center max-w-xs">
                    There's no perfect way to do this. Just breathe at whatever pace feels comfortable.
                </SmallText>
            </main>

            {/* Session Completion Modal */}
            {showCompletion && (
                <SessionCompletion
                    toolName="Box Breathing"
                    toolCategory="breathing"
                    onClose={() => setShowCompletion(false)}
                />
            )}
        </div>
    );
};

export default BoxBreathing;
